// @ts-nocheck
// TODO WIP, this might be useful? Or perhaps there's a better method with playwright, cypress or selenium?

import {
  BridgeControlsType,
  ContractManagerType,
} from "@/features/bridge/providers/bridge.provider";

import { type ChainData } from "@/constants/chain-details";
import {
  ARBITRUM_CHAIN_ID,
  BLESSNET_CHAIN_ID,
} from "@/features/bridge/constants/bridge.constant";
import { BigNumberish, formatEther, parseEther } from "ethers";
import { showToast } from "@/components/layout/toast";
import type {
  blessnetChainAddress,
  arbitrumChainAddress,
} from "@/constants/bless-token-address";
import type { HexString } from "@/types/general.types";
import type { TestType } from "@/features/bridge/types/test.types";
import type { log } from "console";
import type { getBalance } from "viem/actions";

const useIntegrationManager = (
  bridgeControls: BridgeControlsType,
  contractManager: ContractManagerType,
) => {
  const {
    bridgeOut,
    bridgeOutNative,
    bridgeTokens,
    transferAndCall,
    bridgeFromBless,
  } = contractManager;
  const {
    toChain: { setDestinationChain },
    fromChain: { originChain, setOriginChain },
    setAmount,
    account,
  } = bridgeControls;

  /*
	Button calls function
	Logger creates new log file with the name for the test case
	Dialog pops up
	Everytime a transaction is sent and a response is returned
	Bundle up the tx has into the correct link for chain explorer and wormhole explorer
	Log it to the logger in the new log
	Move to the next bridge transaction
	*/

  function createMessage(
    testType: TestType,
    originChain: ChainData,
    destinationChain: ChainData,
    amount: string,
    txn: HexString | undefined,
    originBalance: BigNumberish | undefined,
    destinationBalance: BigNumberish | undefined,
  ): IntegrationLogMessageType {
    return {
      testType: testType,
      networkType: isMainnet() ? "sepolia" : "mainnet",
      from: originChain.chainName,
      to: destinationChain.chainName,
      explorerLink: txn
        ? generateExplorerTxLink(originChain.explorerAddress, txn)
        : "",
      wormholeLink: txn ? generateWormholeTxLink(txn) : "",
      amountSent: amount,
      expectedBalances: {
        origin: (
          Number(formatEther(originBalance!)) - Number(amount)
        ).toString(),
        destination: (
          Number(formatEther(destinationBalance!)) + Number(amount)
        ).toString(),
      },
      txnSuccess: txn ? true : false,
    };
  }

  const AMOUNT_TO_SEND = "1";
  const TEST_FILE_NAME = "integration-test";
  const LOG_BUFFER: IntegrationLogMessageType[] = [];

  async function getBalances(
    originChain: ChainData,
    destinationChain: ChainData,
  ) {
    const oBal = await getBalance(
      originChain.chainId,
      account.address,
      isMainnet(),
    );
    const dBal = await getBalance(
      destinationChain.chainId,
      account.address,
      isMainnet(),
    );
    return { oBal, dBal };
  }

  async function testLog() {
    newLog("integration-test-" + new Date().toString());
    testLogToFile("Hello test test");
  }

  async function runIntegrationTest() {
    if (!account.isConnected) {
      showToast(
        "Please connect a wallet to continue with test.",
        "Wallet not connected!",
      );
      return;
    }
    const response = await newLog(TEST_FILE_NAME);

    if (!response) {
      logError("There was an error creating the log file!");
      return;
    }

    try {
      log("Any to any test");
      await anyToAny();
      const response = await logToFile(LOG_BUFFER);
      if (!response) log("Error logging to file!");
      LOG_BUFFER.length = 0;
      log("Any to bless test");
      await anyToBless();
      log("Arb to bless test");
      await arbToBless();
      log("Bless to any test");
      await blessToAny();
    } catch (error: any) {
      showToast("An error occurred during the test: " + error.message, "Error");
      logError(error.message);
      return;
    }

    log("Integration test completed!");
  }

  // Bridge between them all except bless
  async function anyToAny() {
    for (const originChain of blockChainData()) {
      if (originChain.chainId === blessnetChainAddress(isMainnet())) continue;

      setOriginChain(originChain);

      for (const destinationChain of blockChainData()) {
        if (
          destinationChain.chainId === blessnetChainAddress(isMainnet()) ||
          destinationChain.chainId === originChain.chainId
        )
          continue;
        setAmount(AMOUNT_TO_SEND);
        setDestinationChain(destinationChain);
        const { oBal, dBal } = await getBalances(originChain, destinationChain);
        const txn = await bridgeOut(
          originChain,
          destinationChain,
          AMOUNT_TO_SEND,
          false,
        );
        const message = createMessage(
          TestType.AnyToAny,
          originChain,
          destinationChain,
          AMOUNT_TO_SEND,
          txn,
          oBal,
          dBal,
        );
        LOG_BUFFER.push(message);
        // const response = await logToFile(message);
      }
    }
  }

  // Any one (except arb) to bless
  async function anyToBless() {
    const destinationChain = blockChainData().find(
      (chain) => chain.chainId === blessnetChainAddress(isMainnet()),
    )!;
    setDestinationChain(destinationChain);

    for (const originChain of blockChainData()) {
      if (
        originChain.chainId === blessnetChainAddress(isMainnet()) ||
        originChain.chainId === arbitrumChainAddress()
      )
        continue;

      setOriginChain(originChain);
      setAmount(AMOUNT_TO_SEND);

      const { oBal, dBal } = await getBalances(originChain, destinationChain);
      const txn = await bridgeOutNative(
        originChain,
        destinationChain,
        AMOUNT_TO_SEND,
        false,
      );
      const message = createMessage(
        TestType.AnyToBless,
        originChain,
        destinationChain,
        AMOUNT_TO_SEND,
        txn,
        oBal,
        dBal,
      );
      LOG_BUFFER.push(message);
    }
  }

  // Arb to bless
  async function arbToBless() {
    const originChain = blockChainData().find(
      (chain) => chain.chainId === arbitrumChainAddress(),
    )!;
    const destinationChain = blockChainData().find(
      (chain) => chain.chainId === blessnetChainAddress(isMainnet()),
    )!;
    setOriginChain(originChain);
    setDestinationChain(destinationChain);
    setAmount(AMOUNT_TO_SEND);

    const { oBal, dBal } = await getBalances(originChain, destinationChain);
    const txn = await transferAndCall(AMOUNT_TO_SEND);
    const message = createMessage(
      TestType.ArbitrumToBless,
      originChain,
      destinationChain,
      AMOUNT_TO_SEND,
      txn,
      oBal,
      dBal,
    );
    LOG_BUFFER.push(message);
  }

  // Bless to any
  async function blessToAny() {
    const originChain = blockChainData().find(
      (chain) => chain.chainId === BLESSNET_CHAIN_ID,
    )!;
    setOriginChain(originChain);

    for (const destinationChain of blockChainData()) {
      if (destinationChain.chainId === BLESSNET_CHAIN_ID) continue;
      setDestinationChain(destinationChain);
      setAmount(AMOUNT_TO_SEND);

      const { oBal, dBal } = await getBalances(originChain, destinationChain);
      const txn = await bridgeFromBless(
        destinationChain,
        AMOUNT_TO_SEND,
        false,
      );
      const message = createMessage(
        TestType.BlessToNotArb,
        originChain,
        destinationChain,
        AMOUNT_TO_SEND,
        txn,
        oBal,
        dBal,
      );
      LOG_BUFFER.push(message);
    }
  }

  return {
    runIntegrationTest,
    anyToAny,
    testLog,
  };
};

export default useIntegrationManager;
